// react
import { MouseEvent, MouseEventHandler, ReactNode, useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { NavLink } from 'react-router-dom';

// hooks
import { useAuth } from '../../../hooks/useAuth';

// context
import { ContextUser } from '../../../App.context';

// mui
import { Box, IconButton, SxProps, Typography } from '@mui/material';

// fields
import { SelectLanguage } from '../../fields/SelectLanguage/SelectLanguage';

// icons
import {
	ClearOutlined as IconClear,
	DescriptionOutlined as IconDescription,
	FolderOutlined as IconFolder,
	LogoutOutlined as IconLogout,
	PersonOutlined as IconPerson,
} from '@mui/icons-material';

// props
export interface NavItem {
	className?: string;
	href?: string;
	icon: ReactNode;
	label: string;
	onClick?: MouseEventHandler;
	rel?: string;
	target?: string;
	to?: string;
}

export interface NavProps {
	isOpen?: boolean;
	onClose?: MouseEventHandler;
}

export const Nav = ({ isOpen, onClose }: NavProps) => {
	// hooks
	const { logout } = useAuth();
	const { t } = useTranslation();

	// context
	const { hasSupplier } = useContext(ContextUser);

	// state
	const [isExpanded, setIsExpanded] = useState(false);

	// handlers
	const handleClickNavItem = (e: MouseEvent<HTMLButtonElement>) => {
		setIsExpanded(false);

		if (onClose) {
			onClose(e);
		}
	};

	const items: NavItem[] = [
		{
			icon: <IconFolder />,
			label: t('dashboard'),
			to: '/dashboard',
		},
	];

	if (hasSupplier) {
		items.push({
			icon: <IconDescription />,
			label: t('contracts'),
			to: '/contracts',
		});
	}

	items.push(
		{
			icon: <IconPerson />,
			label: t('profile'),
			to: '/profile',
		},
		{
			icon: <IconDescription />,
			href: '/guide.pdf',
			label: t('userGuide'),
			rel: 'noopener noreferrer',
			target: '_blank',
		},
		{
			icon: <IconLogout />,
			label: t('logout'),
			onClick: () => {
				logout();
			},
		}
	);

	const renderItems = (items: NavItem[]) => {
		return items.map((item, i) => {
			const { className, icon, label, ...itemProps } = item;

			// eslint-disable-next-line
			let Component: any = 'button';

			if (item.to) {
				Component = NavLink;
			} else if (item.href) {
				Component = 'a';
			}

			return (
				<Box
					component="li"
					key={`nav-item-${i}`}
					sx={(theme) => {
						return {
							listStyle: 'none',
						};
					}}>
					<Box
						className={className}
						component={Component}
						onClick={(e: MouseEvent<HTMLButtonElement>) => handleClickNavItem(e)}
						sx={(theme) => {
							let sx: SxProps = {
								transition: 'all 200ms',
								borderRadius: 2,
								display: 'flex',
								alignItems: 'center',
								gap: theme.spacing(1),
								padding: theme.spacing(1),
								width: '100%',
								textDecoration: 'none',
								color: theme.palette.common.white,
								'& svg': {
									fill: theme.palette.brand.blue[400],
								},
								'&:hover, &.active': {
									backgroundColor: theme.palette.brand.blue[600],
								},
							};

							if (isExpanded) {
								sx = {
									...sx,
									justifyContent: 'flex-start',
								};
							}

							return sx;
						}}
						{...itemProps}>
						{icon}
						<Typography
							component="div"
							fontWeight={600}
							sx={(theme) => {
								let sx: SxProps = {};

								if (isExpanded) {
									sx = {
										...sx,
										display: 'inline-flex',
									};
								}

								return sx;
							}}>
							{label}
						</Typography>
					</Box>
				</Box>
			);
		});
	};

	return (
		<Box
			sx={{
				position: 'relative',
			}}>
			<Box
				onClick={() => setIsExpanded(false)}
				sx={(theme) => {
					let sx: SxProps = {
						transition: 'all 200ms',
						backgroundColor: 'white',
						backdropFilter: 'blur(3px)',
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						opacity: 0,
						pointerEvents: 'none',
					};

					return sx;
				}}
			/>
			<Box
				sx={(theme) => {
					let sx: SxProps = {
						transition: 'all 200ms',
						transform: 'translateX(-100%)',
						backgroundColor: theme.palette.secondary.main,
						position: 'fixed',
						top: 0,
						left: 0,
						display: 'flex',
						flexDirection: 'column',
						width: '100%',
						height: `100vh`,
						overflowY: 'auto',
						padding: theme.spacing(12, 2, 2, 2),
						[`${theme.breakpoints.up('md')}`]: {
							padding: theme.spacing(12, 4, 4, 4),
						},
					};

					if (isOpen) {
						sx = {
							...sx,
							transform: 'translateX(0)',
						};
					}

					return sx;
				}}>
				<Box
					component="nav"
					sx={(theme) => {
						return {
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							flex: 1,
							gap: theme.spacing(1),
							width: '100%',
						};
					}}>
					<Box
						sx={(theme) => {
							return {
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
								gap: theme.spacing(1),
								height: '100%',
							};
						}}>
						<Box
							component="ul"
							sx={(theme) => {
								return {
									display: 'flex',
									flexDirection: 'column',
									gap: theme.spacing(1),
									margin: 0,
									padding: 0,
								};
							}}>
							{renderItems(items)}
						</Box>
						<SelectLanguage />
					</Box>
				</Box>

				<Box
					sx={(theme) => {
						return {
							position: 'absolute',
							top: theme.spacing(2),
							right: theme.spacing(3),
						};
					}}>
					<IconButton color="white" onClick={onClose}>
						<IconClear />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
};
